// Modelo NORMALIZADO del documento legal de cotización (formato cotizacion.html).
//
// Tanto QuoteResponse (detalle interno) como QuotePublicResponse (portal) exponen
// los MISMOS campos del documento, pero con algunos nombres de respaldo distintos
// (companyCommercialName vs companyName, sellerFullName vs sellerName, etc.).
// Para que el componente <app-quote-document> sea 100% presentacional y no tenga
// que conocer ambos shapes, se normalizan aquí con dos mappers.

import { QuoteResponse, QuotePublicResponse, QuoteItemResponse, QuoteSignatureView, QuotePaymentType } from '../../models/quote.model';
import { QuoteClauseResponse } from '../../models/quote-clause.model';
import { environment } from '../../../../../environments/environment';

/** Datos del documento legal, normalizados e independientes del origen. */
export interface QuoteDocumentData {
  // Cabecera
  quoteNumber: string;
  version: number;
  /**
   * Vista pública (portal del cliente). Cuando es true, la versión interna (v{n}) NO se muestra
   * en el documento. La vista interna del CRM la deja en false/undefined y sí muestra la versión.
   */
  isPublic?: boolean;
  statusName: string;
  datetimeIssued?: string | null;
  datetimeExpires?: string | null;
  validityDays?: number | null;
  /** Etiqueta legible de la moneda (ej. "Soles (S/.)"). */
  currencyLabel: string;
  /** Símbolo para montos (ej. "S/"). */
  currencySymbol: string;
  /** Color de marca; respaldo al brand del CRM. */
  accentColor: string;
  /**
   * (INC_CRM_005 (5) inc39) Color secundario de la plantilla: acento de apoyo (etiquetas
   * Proveedor/Cliente y fondo del bloque TOTAL), en línea con lo que hace el PDF.
   * Si la plantilla no lo define cae en accentColor y el documento se ve igual que antes.
   */
  secondaryColor: string;
  /**
   * (INC29/30) Logo del documento. Precedencia: templateLogoFileId (misma fuente que el PDF) >
   * providerLogoUrl (logo del negocio) > templateLogoUrl (absoluta y congelada en el upload).
   */
  logoUrl?: string | null;
  /** (INC31) Texto de encabezado configurado en la plantilla; se muestra bajo la cabecera. */
  headerText?: string | null;
  /** (INC31) Bloque de contacto del proveedor definido en la plantilla (puede ser multilínea). */
  contactInfo?: string | null;

  // Partes
  providerName: string;
  providerTaxId?: string | null;
  providerAddress?: string | null;
  providerEmail?: string | null;
  providerPhone?: string | null;
  clientCompanyName: string;
  clientTaxId?: string | null;
  clientContactName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  clientAddress?: string | null;

  // Mensaje de apertura (visible al cliente)
  openingMessage?: string | null;

  // Ítems (V75: QuoteItemResponse ya incluye itemCode + itemTaxRate, usados por la tabla de 9 columnas;
  // V76: + category, usado por el agrupado por categoría)
  items: QuoteItemResponse[];

  // ---- Opciones de presentación del documento (V76) ----
  /** Si true, agrupa los ítems por categoría con subencabezados; si false/undefined, tabla plana. */
  groupByCategory: boolean;
  /** Si false, oculta la columna Código; si true/undefined, se muestra. */
  showItemCodes: boolean;

  // Totales
  subtotal: number;
  taxEnabled: boolean;
  tax: number;
  taxLabel?: string | null;
  /** % del impuesto ya calculado (ej. 18). */
  taxRatePercent: number;
  globalDiscount: number;
  totalAmount: number;

  // Términos y cláusulas
  termsConditions?: string | null;
  clauses: QuoteClauseResponse[];

  // Condiciones comerciales (V74) — todas opcionales; el bloque solo muestra las que tengan valor.
  paymentType?: QuotePaymentType | null;
  /** Etiqueta legible de la forma de pago; si no viene, se deriva de paymentType. */
  paymentTypeLabel?: string | null;
  creditTermDays?: number | null;
  advancePercent?: number | null;
  /** Tasa de interés / financiamiento (%) aplicada al saldo financiado en pagos a crédito. null o 0 = sin interés. */
  interestRatePercent?: number | null;
  deliveryTime?: string | null;
  warranty?: string | null;
  deliveryPlace?: string | null;
  exchangeRate?: number | null;
  /**
   * (V188) Moneda DESTINO del tipo de cambio, resuelta del catálogo. Es la que identifica
   * el total convertido ("Total en Dólar Estadounidense (USD): $ 296.80"). Null cuando la
   * tasa se tecleó a mano: entonces el renglón cae al formato histórico "Total (T.C. x)".
   */
  exchangeCurrencyCode?: string | null;
  exchangeCurrencyName?: string | null;
  exchangeCurrencySymbol?: string | null;

  // Firmas (separadas por tipo)
  approverSignature: QuoteSignatureView | null;
  clientSignature: QuoteSignatureView | null;

  // Notas / pie
  notes?: string | null;
  documentHash?: string | null;
  templateFooterText?: string | null;
}

const DEFAULT_ACCENT = '#1c50a0';

function taxRatePercent(rate?: number | null): number {
  return Math.round(((rate ?? 0) * 100) * 100) / 100;
}

/**
 * (INC29/30) URL del logo con la precedencia acordada con el backend.
 *
 * `templateLogoFileId` es la MISMA fuente que embebe el PDF, por eso va primero: así el paso 4 del
 * wizard, el detalle y el portal público muestran el mismo logo. La URL se arma RELATIVA a
 * `environment.apiUrl` (que ya termina en "/": 'http://localhost:8090/' en dev, '/' en prod), en vez
 * de usar `templateLogoUrl`, que es absoluta y se rompe si APP_BASE_URL apunta a otro host.
 */
function resolveLogoUrl(
  templateLogoFileId?: number | null,
  providerLogoUrl?: string | null,
  templateLogoUrl?: string | null
): string | null {
  if (templateLogoFileId != null) {
    return `${environment.apiUrl}api/files/${templateLogoFileId}/download`;
  }
  return providerLogoUrl || templateLogoUrl || null;
}

function firstSignature(signatures: QuoteSignatureView[] | undefined, type: 'APPROVER' | 'CLIENT'): QuoteSignatureView | null {
  return (signatures || []).filter(s => s.signerType === type)[0] || null;
}

/**
 * Construye el documento desde la respuesta del detalle interno (QuoteResponse).
 *
 * @param asClientView (Incidente 16) cuando es `true` el documento se trata como
 *   la vista del cliente (la que se le envía): oculta el indicador de versión "(vN)".
 *   La vista interna del CRM (detalle de la cotización) lo deja en `false` para
 *   conservar la versión como dato de control interno.
 */
export function quoteDocumentFromResponse(q: QuoteResponse, asClientView = false): QuoteDocumentData {
  const symbol = q.currencySymbol || q.currencyCode || '';
  return {
    quoteNumber: q.quoteNumber,
    version: q.version,
    isPublic: asClientView,
    statusName: q.statusName,
    datetimeIssued: q.datetimeIssued,
    datetimeExpires: q.datetimeExpires || q.validUntil,
    validityDays: q.validityDays,
    currencyLabel: q.currencyLabel || q.currencyCode || symbol || '',
    currencySymbol: symbol,
    accentColor: q.templatePrimaryColor || DEFAULT_ACCENT,
    secondaryColor: q.templateSecondaryColor || q.templatePrimaryColor || DEFAULT_ACCENT,
    logoUrl: resolveLogoUrl(q.templateLogoFileId, q.providerLogoUrl, q.templateLogoUrl),
    headerText: q.templateHeaderText,
    contactInfo: q.templateContactInfo,

    providerName: q.providerName || q.sellerFullName || '',
    providerTaxId: q.providerTaxId,
    providerAddress: q.providerAddress,
    providerEmail: q.sellerEmail,
    providerPhone: q.providerPhone,
    clientCompanyName: q.clientCompanyName || q.companyCommercialName || '',
    clientTaxId: q.clientTaxId,
    clientContactName: q.clientContactName || q.contactFullName,
    clientEmail: q.clientEmail || q.contactEmail,
    clientPhone: q.clientPhone || q.contactPhone,
    clientAddress: q.clientAddress,

    openingMessage: q.openingMessage,

    items: q.items || [],
    groupByCategory: q.groupByCategory ?? false,
    showItemCodes: q.showItemCodes ?? true,

    subtotal: q.subtotal,
    taxEnabled: q.taxEnabled ?? (q.tax > 0),
    tax: q.tax,
    taxLabel: q.taxLabel,
    taxRatePercent: taxRatePercent(q.taxRate),
    globalDiscount: q.globalDiscount ?? q.discount ?? 0,
    totalAmount: q.totalAmount,

    // (INC31) Fallback de la plantilla, igual que el PDF: si la cotización no trae términos propios.
    termsConditions: q.termsConditions || q.templateDefaultTerms,
    clauses: q.clauses || [],

    paymentType: q.paymentType,
    paymentTypeLabel: q.paymentTypeLabel,
    creditTermDays: q.creditTermDays,
    advancePercent: q.advancePercent,
    interestRatePercent: q.interestRatePercent,
    deliveryTime: q.deliveryTime,
    warranty: q.warranty,
    deliveryPlace: q.deliveryPlace,
    exchangeRate: q.exchangeRate,
    exchangeCurrencyCode: q.exchangeRateCurrencyCode,
    exchangeCurrencyName: q.exchangeRateCurrencyName,
    exchangeCurrencySymbol: q.exchangeRateCurrencySymbol,

    approverSignature: firstSignature(q.signatures, 'APPROVER'),
    clientSignature: firstSignature(q.signatures, 'CLIENT'),

    notes: q.notes,
    documentHash: q.documentHash,
    templateFooterText: q.templateFooterText,
  };
}

/** Construye el documento desde la respuesta pública (QuotePublicResponse). */
export function quoteDocumentFromPublic(q: QuotePublicResponse): QuoteDocumentData {
  const symbol = q.currencySymbol || q.currencyCode || '';
  return {
    quoteNumber: q.quoteNumber,
    version: q.version,
    isPublic: true,
    statusName: q.statusName,
    datetimeIssued: q.datetimeIssued,
    datetimeExpires: q.datetimeExpires,
    validityDays: q.validityDays,
    currencyLabel: q.currencyLabel || q.currencyCode || symbol || '',
    currencySymbol: symbol,
    accentColor: q.templatePrimaryColor || DEFAULT_ACCENT,
    secondaryColor: q.templateSecondaryColor || q.templatePrimaryColor || DEFAULT_ACCENT,
    // GET /api/files/{id}/download es público: funciona sin JWT en el portal del cliente.
    logoUrl: resolveLogoUrl(q.templateLogoFileId, q.providerLogoUrl, q.templateLogoUrl),
    headerText: q.templateHeaderText,
    contactInfo: q.templateContactInfo,

    providerName: q.providerName || q.sellerName || '',
    providerTaxId: q.providerTaxId,
    providerAddress: q.providerAddress,
    providerEmail: undefined,
    providerPhone: q.providerPhone,
    clientCompanyName: q.clientCompanyName || q.companyName || '',
    clientTaxId: q.clientTaxId,
    clientContactName: q.clientContactName || q.contactName,
    clientEmail: q.clientEmail,
    clientPhone: q.clientPhone,
    clientAddress: q.clientAddress,

    openingMessage: q.openingMessage,

    items: q.items || [],
    groupByCategory: q.groupByCategory ?? false,
    showItemCodes: q.showItemCodes ?? true,

    subtotal: q.subtotal,
    taxEnabled: q.taxEnabled ?? (q.tax > 0),
    tax: q.tax,
    taxLabel: q.taxLabel,
    taxRatePercent: taxRatePercent(q.taxRate),
    globalDiscount: q.globalDiscount ?? 0,
    totalAmount: q.totalAmount,

    // (INC31) Fallback de la plantilla, igual que el PDF: si la cotización no trae términos propios.
    termsConditions: q.termsConditions || q.templateDefaultTerms,
    clauses: q.clauses || [],

    paymentType: q.paymentType,
    paymentTypeLabel: q.paymentTypeLabel,
    creditTermDays: q.creditTermDays,
    advancePercent: q.advancePercent,
    interestRatePercent: q.interestRatePercent,
    deliveryTime: q.deliveryTime,
    warranty: q.warranty,
    deliveryPlace: q.deliveryPlace,
    exchangeRate: q.exchangeRate,
    exchangeCurrencyCode: q.exchangeRateCurrencyCode,
    exchangeCurrencyName: q.exchangeRateCurrencyName,
    exchangeCurrencySymbol: q.exchangeRateCurrencySymbol,

    approverSignature: firstSignature(q.signatures, 'APPROVER'),
    clientSignature: firstSignature(q.signatures, 'CLIENT'),

    notes: q.notes,
    documentHash: q.documentHash,
    templateFooterText: q.templateFooterText,
  };
}
